import { StageInfo, StageGroup, GroupedStage, STAGE_TYPES, HIDDEN_STAGE_TYPE } from '../types/stageTypes';

/**
 * Groups stages by their type (INTRODUCED, INTERVIEW, etc) or special categories (UNMAPPED, HIDDEN)
 * @param stages Array of stage information objects
 * @returns Array of stage groups
 */
export const groupStagesByType = (stages: StageInfo[]): StageGroup[] => {
    // Create groups for each stage type, including UNMAPPED and HIDDEN groups
    const groups: StageGroup[] = [
        {
            stageType: 'UNMAPPED',
            label: 'Not Mapped',
            stages: [],
            isExpanded: true
        },
        ...STAGE_TYPES.map(type => ({
            stageType: type.value,
            label: type.label,
            stages: [],
            isExpanded: true
        })),
        // Add the HIDDEN group at the end
        {
            stageType: HIDDEN_STAGE_TYPE,
            label: 'Hidden for Agencies',
            stages: [],
            isExpanded: true
        }
    ];

    // Create a Set to track stage names that have been processed
    const processedStageNames = new Set<string>();

    // Map each stage to its group
    stages.forEach(stage => {
        // Normalize the stage name for duplicate checking
        const normalizedName = stage.name.toLowerCase().trim();

        // Skip if we've already processed a stage with this name
        if (processedStageNames.has(normalizedName)) {
            return;
        }

        // Mark this stage name as processed
        processedStageNames.add(normalizedName);

        // Create a stage object
        const groupedStage: GroupedStage = {
            id: stage.id,
            name: stage.name,
            originalId: stage.id, // Use original ID for reference
            mapping: {
                ...stage.mapping,
                stage_type: stage.mapping.stage_type
            },
            isNew: stage.isNew,
            isChanged: stage.isChanged
        };

        // Add to the appropriate group based on is_unmapped and stage_type
        if (stage.mapping.is_unmapped) {
            // Add to HIDDEN group (last group)
            groups[groups.length - 1].stages.push(groupedStage);
        } else if (!stage.mapping.stage_type) {
            // Add to UNMAPPED group (first group)
            groups[0].stages.push(groupedStage);
        } else {
            // Find the correct mapped group by stage type
            const groupIndex = groups.findIndex(g => g.stageType === stage.mapping.stage_type);
            // Ensure it's not the UNMAPPED (index 0) or HIDDEN (last index) group
            if (groupIndex > 0 && groupIndex < groups.length - 1) {
                groups[groupIndex].stages.push(groupedStage);
            }
        }
    });

    // Sort interview stages by order
    const interviewGroupIndex = groups.findIndex(g => g.stageType === 'INTERVIEW');
    if (interviewGroupIndex > 0 && interviewGroupIndex < groups.length - 1) {
        // First sort by order
        groups[interviewGroupIndex].stages.sort((a, b) =>
            (a.mapping.order || 0) - (b.mapping.order || 0)
        );

        // Then ensure sequential ordering (no gaps)
        const interviewStages = groups[interviewGroupIndex].stages;
        interviewStages.forEach((stage, index) => {
            if (stage.mapping.order !== index) {
                stage.mapping.order = index;
                stage.isChanged = true;
            }
        });
    }

    // Return all groups, including empty ones
    return groups;
};

/**
 * Filters stage groups based on a search query
 * @param groups Array of stage groups
 * @param searchQuery Search string to filter by
 * @returns Filtered array of stage groups
 */
export const filterStageGroups = (groups: StageGroup[], searchQuery: string): StageGroup[] => {
    return groups.map(group => {
        // Filter stages within the group
        const filteredStages = group.stages.filter(stage => {
            // Apply search filter
            return !searchQuery ||
                stage.name.toLowerCase().includes(searchQuery.toLowerCase());
        });

        // Return the group with filtered stages
        return {
            ...group,
            stages: filteredStages
        };
    });
}; 